import React, { useEffect, useMemo, useCallback, useState, useRef } from "react";
import {
  Box,
  IconButton,
  Popper,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  ArrowUpwardIcon,
  AttachFileIcon,
  CloseIcon,
  ExpandLessIcon,
  StopIcon,
  UploadFileIcon,
} from "../../../shared/icons";

import {
  ChatInput,
  InputContainer,
  InputRowContainer,
  MessageTextarea,
  ButtonRowContainer,
  LeftButtonsContainer,
  RightButtonsContainer,
  InputIconButton,
  FilePreviewContainer,
  FilePreviewItem,
  FilePreviewImage,
  FilePreviewIcon,
  FileInfo,
  FileName,
  FileDetails,
  UploadDropdown,
  UploadOption,
  ModeSelectorButton,
  TextareaContainer,
  getChatRadii,
  getChatBorders,
} from "./AIChat.styles";
import {
  MODELS,
  DEFAULT_MODEL,
  MODEL_COPY,
  MULTI_MODEL_OPTIONS,
  findMultiModelOption,
  groupModelsByCapability,
  isRetiredModelId,
} from "./constants";
import { useClickOutside } from "../../../shared/utils";
import { useOrgAccess } from "../../../shared/contexts";

const AIInputSurface = ({
  composer,
  selectedModel = DEFAULT_MODEL,
  onModelChange = null,
  isLoading = false,
  minCharacters = 3,
  placeholder = "How can I help you?",
  allowAttachments = true,
  allowModeSelection = false,
  allowModelSelection = false,
  // Tri-state on purpose. `true` = the caller knows the workspace has the
  // multi-model entitlement, `false` = it knows the workspace does not, and
  // `null` (the default) = nobody knows, which is today's honest answer since
  // the entitlement is not client-readable. Only `false` changes what is drawn:
  // it hides the opencode-routed options. `true` and `null` are the same
  // picture now that the entitlement defaults on and there is no caveat to
  // attach. See the gating block below.
  multiModelAvailable = null,
  uploadSupportText = "Supports PDF, DOCX, TXT, PNG, JPG, GIF",
  onSend = () => { },
  onStopRequest = () => { },
  className,
  dataTestId,
  disabled = false,
  forceDisableSend = false,
  renderTextareaOverlay,
  showNoCreditCardMessage = false,
  awaitingPeerLabel = null,
  // Slash commands: '/' at the start of the composer opens this list
  // (workflows by name, plus the built-in /compact).
  slashCommands = [],
  onCompactChat = null,
  onCancelPeerWait = null,
  liveDataAvailable = false,
  liveDataMode = false,
  onLiveDataModeChange = null,
  privateSessionActive = false,
}) => {
  const theme = useTheme();
  const chatRadii = getChatRadii(theme);
  const chatBorders = getChatBorders(theme);
  // Submission window. `isLoading` is NOT true for the whole time a request is
  // in flight: the send sets it, and the SSE teardown that follows a session
  // attach clears it again (hooks.js finalizeCurrentTurn runs on ANY stream
  // close, including the deliberate abort of the previous stream in
  // ensureSessionStream). It only comes back when the new stream's first turn
  // event lands, which is also when the first content renders. That is the
  // several-second window in which the composer fell back to the idle arrow
  // with nothing else on screen, so the pane read as crashed.
  //
  // The composer can answer the question for itself: `onSend` returns the send
  // promise, which settles when the turn ends AND when the send is refused at
  // admission. So the control is optimistic but never stuck: a refused send
  // puts the arrow back instead of trapping the user behind a stop button for
  // a turn that never started.
  const [sendPending, setSendPending] = useState(false);
  const trackSend = useCallback((sendResult) => {
    setSendPending(true);
    const settle = () => setSendPending(false);
    // then(settle, settle) rather than finally(): a rejected send promise must
    // not surface here as an unhandled rejection.
    Promise.resolve(sendResult).then(settle, settle);
  }, []);
  // Cross-app waiting window: the LOCAL turn has ended (send_message to a
  // peer app is fire-and-forget) but the peer is still working on our
  // request. Keep the ongoing-work visuals up so the request never looks
  // finished while Excel/Word/PowerPoint is mid-task on our behalf.
  const isAwaitingPeer = !isLoading && !!awaitingPeerLabel;
  // Our own request is in flight from the click, not from the first event.
  const isOwnTurnActive = isLoading || sendPending;
  const isTaskActive = isOwnTurnActive || isAwaitingPeer;

  // Destructure composer props FIRST - before any hooks that reference them
  const {
    input = "",
    handleInputChange = () => { },
    handleKeyDown = () => { },
    filePreviews = [],
    formatFileSize = () => "",
    getFileTypeInfo = () => ({
      name: "FILE",
      icon: UploadFileIcon,
    }),
    removeAttachmentAt = () => { },
    handleFileUpload = () => { },
    addAttachmentFiles = () => { },
    fileInputRef = { current: null },
    textareaRef = { current: null },
    setInput = () => { },
    selectedFiles = [],
  } = composer || {};

  // Local state for dropdown anchors
  const [showModelDropdown, setShowModelDropdown] = React.useState(false);
  const [modelDropdownAnchorEl, setModelDropdownAnchorEl] = React.useState(null);

  const closeModelDropdown = useCallback(() => {
    setShowModelDropdown(false);
    setModelDropdownAnchorEl(null);
  }, [setModelDropdownAnchorEl, setShowModelDropdown]);

  const uploadDropdownModifiers = useMemo(() => ([
    { name: "offset", options: { offset: [0, 8] } },
    { name: "flip", options: { fallbackPlacements: ["top-start", "top", "bottom-start"] } },
    { name: "preventOverflow", options: { padding: 8 } },
  ]), []);

  const modelDropdownModifiers = useMemo(() => ([
    { name: "offset", options: { offset: [0, 8] } },
    { name: "flip", options: { fallbackPlacements: ["bottom-end", "bottom-start"] } },
    { name: "preventOverflow", options: { padding: 8 } },
  ]), []);

  useClickOutside([
    {
      isOpen: showModelDropdown,
      selector: '[data-model-dropdown-scope="true"]',
      onClose: closeModelDropdown
    }
  ]);

  useEffect(() => {
    if (!showModelDropdown) {
      setModelDropdownAnchorEl(null);
    }
  }, [showModelDropdown, setModelDropdownAnchorEl]);

  const safeInput = input ?? "";
  const safeSelectedFiles = selectedFiles ?? [];
  const trimmedInputLength = safeInput.trim().length;
  const hasDraft = trimmedInputLength > 0 || safeSelectedFiles.length > 0;
  const computedPlaceholder = useMemo(() => {
    if (trimmedInputLength > 0 && trimmedInputLength < minCharacters) {
      const remaining = Math.max(minCharacters - trimmedInputLength, 0);
      return `${remaining} more character${remaining === 1 ? "" : "s"} needed...`;
    }
    return placeholder;
  }, [minCharacters, placeholder, trimmedInputLength]);

  const isSendDisabled = forceDisableSend || (!isLoading
    && !disabled
    && trimmedInputLength < minCharacters
    && safeSelectedFiles.length === 0);
  // A blank composer controls the running task (Stop). As soon as the user
  // types or attaches context, the same control sends that update into the
  // live turn. This keeps steering discoverable without adding another row.
  const showStopControl = isTaskActive && (!hasDraft || privateSessionActive);
  const activePlaceholder = privateSessionActive
    ? 'Live data is being written to the open file'
    : isOwnTurnActive
    // Short on purpose. On one row the input is about 160px wide in a 375px
    // pane, so a placeholder much longer than this is cut off mid-sentence.
    ? 'Steer this request'
    : isAwaitingPeer
      ? 'Add another request'
      : liveDataMode
        ? 'Describe the licensed data to write into this file'
        : computedPlaceholder;

  // ---------------------------------------------------------------------
  // Slash commands. Typing '/' as the first character opens a menu of the
  // user's workflows plus the built-in /compact. On send, '/name rest' is
  // rewritten to the skill-invocation phrasing the model routes to
  // read_skill; '/compact' triggers conversation compaction directly.
  // ---------------------------------------------------------------------
  const inputRowRef = useRef(null);
  const slashActive = typeof input === 'string' && input.startsWith('/') && !input.includes(' ');
  const slashQuery = slashActive ? input.slice(1).toLowerCase() : '';
  const slashItems = useMemo(() => {
    if (!slashActive) return [];
    const base = [
      ...(onCompactChat ? [{ name: 'compact', description: 'Summarize older history so this chat can keep going', builtin: true }] : []),
      ...slashCommands.map((w) => ({ name: w.name, description: w.description || '', tag: w.tag || '' })),
    ];
    return base.filter((item) => item.name.toLowerCase().startsWith(slashQuery)).slice(0, 12);
  }, [slashActive, slashQuery, slashCommands, onCompactChat]);

  const performSend = useCallback(() => {
    const raw = (input || '').trim();
    if (!liveDataMode && raw.startsWith('/')) {
      const [cmd, ...restParts] = raw.slice(1).split(/\s+/);
      const rest = restParts.join(' ').trim();
      const cmdLower = (cmd || '').toLowerCase();
      if (cmdLower === 'compact' && onCompactChat) {
        onCompactChat();
        setInput('');
        return;
      }
      const workflow = slashCommands.find((w) => String(w.name).toLowerCase() === cmdLower);
      if (workflow) {
        // Model-facing phrasing stays "skill" (that is the tool vocabulary
        // the prompt documents); the UI vocabulary is "workflow".
        trackSend(onSend(`Use the "${workflow.name}" skill${rest ? `: ${rest}` : ''}`));
        return;
      }
    }
    trackSend(onSend());
  }, [input, liveDataMode, onCompactChat, onSend, setInput, slashCommands, trackSend]);

  // The elapsed timer used to live here, beside the Stop button. It now rides
  // the status line under the newest tool card (StreamElapsedTimer), because
  // the user watches the work, not the Stop control. Removed here rather than
  // left in place: two timers counting the same turn in two places is worse
  // than either one alone.

  const textareaOverlay = typeof renderTextareaOverlay === "function"
    ? renderTextareaOverlay()
    : renderTextareaOverlay;

  // Model selection handlers
  const isModelToggleDisabled = typeof onModelChange !== "function";

  const handleModelToggle = useCallback((event) => {
    if (isModelToggleDisabled) {
      return;
    }

    const anchor = event.currentTarget;
    setShowModelDropdown((prev) => {
      const next = !prev;
      if (next) {
        setModelDropdownAnchorEl(anchor);
      } else {
        setModelDropdownAnchorEl(null);
      }
      return next;
    });
  }, [isModelToggleDisabled, setModelDropdownAnchorEl, setShowModelDropdown]);

  // A selection this build has retired (Fable 5, GPT-5.5 Pro) is corrected
  // before anything else looks at it. Unlike the multi-model reset below it
  // does not wait on org access: a retired id is never valid for anyone, so
  // there is no state in which leaving it alone is right. Without this the
  // stored id keeps being posted every turn under a chip naming a different
  // model.
  useEffect(() => {
    if (isRetiredModelId(selectedModel) && onModelChange) {
      onModelChange(DEFAULT_MODEL);
    }
  }, [selectedModel, onModelChange]);

  // ---------------------------------------------------------------------
  // Multi-model tier gating.
  //
  // One condition the client can actually see: a firm workspace. The tier
  // binds its credential grant to the org, so a pod with no org is refused
  // outright. The user's OWN doc carries orgId and is client-readable, so this
  // is a real check and the options that would certainly fail are never
  // offered.
  //
  // The entitlement itself (entitlements.multiModel) is no longer disclosed
  // here. It defaults to ON for every org, so a warning about it would be
  // wrong for essentially every user who read it. A caller that positively
  // knows the workspace lacks it can still pass multiModelAvailable={false}
  // and the options are hidden rather than annotated.
  const { orgId, isLoading: isOrgAccessLoading } = useOrgAccess();
  const multiModelAnswerGiven = multiModelAvailable !== null
    && multiModelAvailable !== undefined;
  // An answer from the caller wins and must be literally `true`, so anything
  // else it passes fails closed rather than reading as permission.
  const multiModelOffered = multiModelAnswerGiven
    ? multiModelAvailable === true
    : !!orgId;

  // Same defense as the Pro+ reset above, for the same reason. A qualified id
  // that this surface no longer offers (the user left the firm, or the caller
  // turned the tier off) would keep being sent and keep being refused, one
  // dead turn per attempt, with nothing on screen explaining why. Waiting for
  // org access to load first matters: on the first render orgId is legitimately
  // still null, and resetting then would clobber a valid selection.
  useEffect(() => {
    if (isOrgAccessLoading) return;
    if (multiModelOffered) return;
    if (findMultiModelOption(selectedModel) && onModelChange) {
      onModelChange(DEFAULT_MODEL);
    }
  }, [isOrgAccessLoading, multiModelOffered, selectedModel, onModelChange]);

  const selectedMultiModel = multiModelOffered
    ? findMultiModelOption(selectedModel)
    : null;

  // Fallback is 'opus' to match DEFAULT_MODEL: an unknown or stale id in the
  // picker chip must not render as Haiku, which was the prior bug.
  let currentModelKey = 'opus';
  if (selectedModel === MODELS.HAIKU) {
    currentModelKey = 'haiku';
  } else if (selectedModel === MODELS.SONNET) {
    currentModelKey = 'sonnet';
  } else if (selectedModel === MODELS.OPUS) {
    currentModelKey = 'opus';
  }

  // Every model names itself on the chip, exactly as it does in the list.
  const currentModelCopy = selectedMultiModel
    ? {
        label: selectedMultiModel.label,
        tooltip: selectedMultiModel.tooltip,
      }
    : MODEL_COPY[currentModelKey];

  // Every model this surface can offer, in source order. Claude leads because
  // it is the product and the default, and because source order is what breaks
  // a tie inside a section (see groupModelsByCapability). The opencode-routed
  // models join only when the workspace can reach them; the grouping then drops
  // any section they leave empty, so a Claude-only pane never draws a heading
  // over nothing.
  //
  // Makers are deliberately interleaved rather than kept apart: the whole
  // point of grouping by capability is that the row under Claude Sonnet is
  // another maker's model at the same capability and a different price.
  const modelSections = groupModelsByCapability([
    { id: 'haiku', model: MODELS.HAIKU, ...MODEL_COPY.haiku },
    { id: 'sonnet', model: MODELS.SONNET, ...MODEL_COPY.sonnet },
    { id: 'opus', model: MODELS.OPUS, ...MODEL_COPY.opus },
    ...(multiModelOffered ? MULTI_MODEL_OPTIONS : []),
  ]);

  // THE ONE SIGNAL LEFT ON A ROW.
  //
  // Capability used to be a four-dot meter here, beside the cost marks. It is
  // gone, and the section header replaced it: a row of dots under a heading
  // that already reads "Pro" states the same fact twice, and two meters on one
  // row is a decode step every time the user opens the menu. Name plus cost is
  // the whole row now.
  //
  // What survives from the two-meter pass are the fixes that were real defects
  // rather than consequences of having two meters:
  //
  //   1. Contrast and size. The marks were 10px at ~0.5 alpha, which at this
  //      pane's real width is a grey smudge: readable only by leaning in, and
  //      not comparable between rows, which is the only job they have. They
  //      are now 11px semibold at 0.7.
  //   2. A shared left origin. They used to be right-aligned in a fixed box,
  //      so `$` and `$$$$` began at different x positions and length could not
  //      be compared down the column. They now grow rightward from a fixed
  //      origin. (The old code's comment claimed exactly this;
  //      `textAlign: 'right'` did the opposite.)
  //   3. The selected row gets MORE weight, not a different hue. The obvious
  //      move was to tint it with primary.main alongside the label; measured
  //      against the real theme that is wrong in both modes. On this surface
  //      primary.main is a neutral (#333333 light, #ffffff dark), so in light
  //      it is indistinguishable from text.primary, and in dark it is the SAME
  //      white now sitting on the selected row's lighter 8% tint, which makes
  //      the chosen model's signal the least legible in the list instead of
  //      the most.
  const signalColor = (selected, base, lifted) =>
    alpha(theme.palette.text.primary, selected ? lifted : base);

  const COST_WORDS = { 1: 'low', 2: 'moderate', 3: 'high', 4: 'highest' };
  const renderCostMarks = (level = 1, selected = false) => (
    <Box
      aria-hidden="true"
      data-signal="cost"
      data-level={level}
      sx={{
        flexShrink: 0,
        // Fixed width sized for the widest level, left aligned: the marks grow
        // rightward from a shared origin instead of shifting per row.
        width: theme.spacing(3.5),
        textAlign: 'left',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: theme.typography.pxToRem(11),
        fontWeight: 600,
        letterSpacing: '0.04em',
        lineHeight: 1,
        color: signalColor(selected, 0.7, 0.92),
      }}
    >
      {'$'.repeat(Math.max(1, Math.min(4, level)))}
    </Box>
  );

  // Render the attachment-chip strip. Positioned inside the input container
  // (just above the textarea) so chips visually belong to the message they're
  // attached to, instead of floating in space above the box.
  const attachmentStrip = allowAttachments && filePreviews.length > 0 ? (
    <FilePreviewContainer>
      {filePreviews.map((preview, index) => {
        const fileTypeInfo = getFileTypeInfo(preview.type, preview.name);
        const FileTypeIcon = fileTypeInfo.icon || UploadFileIcon;
        return (
          <FilePreviewItem key={preview.id || index}>
            {preview.imageUrl ? (
              <FilePreviewImage
                src={preview.imageUrl}
                alt={preview.name}
                onLoad={() => {
                  URL.revokeObjectURL(preview.imageUrl);
                }}
              />
            ) : (
              <FilePreviewIcon>
                <FileTypeIcon />
              </FilePreviewIcon>
            )}

            <FileInfo>
              <FileName>{preview.name}</FileName>
              <FileDetails>
                {formatFileSize(preview.size)} • {fileTypeInfo.name}
              </FileDetails>
            </FileInfo>

            <IconButton
              size="small"
              onClick={() => removeAttachmentAt(index)}
              aria-label={`Remove ${preview.name}`}
              sx={{
                width: theme.spacing(2.5),
                height: theme.spacing(2.5),
                flexShrink: 0,
                color: "text.secondary",
                backgroundColor: 'transparent',
                "&:hover": {
                  color: "error.main",
                  backgroundColor: alpha(theme.palette.error.main, 0.1),
                },
              }}
            >
              <CloseIcon sx={{ fontSize: theme.typography.pxToRem(14) }} />
            </IconButton>
          </FilePreviewItem>
        );
      })}
    </FilePreviewContainer>
  ) : null;

  return (
    <ChatInput className={className} data-testid={dataTestId}>
      <Box sx={{ position: 'relative' }}>
        <InputContainer>
          {attachmentStrip}
          <InputRowContainer
            isLoading={isOwnTurnActive}
            ref={inputRowRef}
            aria-busy={isTaskActive}
            sx={{
              // ONE ROW. The controls sit on the same line as the text instead
              // of on a strip under it: the composer at rest goes from 94px
              // tall to 46px, which is 48px of a narrow pane handed back to the
              // transcript.
              //
              // flex-end, not center: the textarea autogrows (its height is set
              // inline by hooks.js up to 240px, then it scrolls), and the
              // controls have to stay on the last line rather than drift to the
              // middle of a tall input.
              flexDirection: 'row',
              alignItems: 'flex-end',
              flexWrap: 'nowrap',
            }}
          >
            <TextareaContainer
              sx={{
                // Basis 0 so the input takes what the controls leave rather
                // than bidding its own intrinsic width against them, with a
                // floor so a long model name cannot squeeze the text to
                // nothing. Below the floor the deficit goes to the controls,
                // where the model chip is the only shrinkable part.
                flex: '1 1 0%',
                minWidth: theme.spacing(11),
              }}
            >
              {textareaOverlay}
              <MessageTextarea
                ref={textareaRef}
                value={safeInput}
                onChange={handleInputChange}
                onKeyDown={(event) => handleKeyDown(event, {
                  // performSend handles slash commands (/workflow, /compact)
                  // before falling through to a normal send.
                  onSend: performSend,
                  // isLoading intentionally omitted: Enter while a turn is
                  // running sends a STEERING message into the live turn
                  // (handleSend routes it mid-turn server-side).
                  disabled: disabled
                    || forceDisableSend
                    || showStopControl
                    || isSendDisabled
                })}
                onPaste={(event) => {
                  // Clipboard images (screenshots) become attachments; text
                  // paste is untouched. Pasted images arrive named "image.png",
                  // so stamp a readable unique name.
                  if (!allowAttachments) return;
                  const items = Array.from(event.clipboardData?.items || []);
                  const images = items
                    .filter((item) => item.kind === 'file' && item.type.startsWith('image/'))
                    .map((item) => item.getAsFile())
                    .filter(Boolean);
                  if (images.length === 0) return;
                  event.preventDefault();
                  const stamp = Date.now();
                  addAttachmentFiles(images.map((file, i) => new File(
                    [file],
                    `pasted-image-${stamp}${images.length > 1 ? `-${i + 1}` : ''}.${file.type.split('/')[1] || 'png'}`,
                    { type: file.type }
                  )));
                }}
                placeholder={activePlaceholder}
                aria-label={activePlaceholder}
                disabled={disabled || privateSessionActive}
                sx={{
                  // The input is narrower on one row, so a placeholder that
                  // used to fit now wraps. The box is pinned to one line high
                  // when empty (hooks.js sets the height inline and autosize
                  // only runs on real content), so a wrapped placeholder gets
                  // sliced through the middle of its second line. One line that
                  // runs out of room reads as overflow; half a row of letters
                  // reads as breakage.
                  //
                  // text-overflow is set for the engines that honour it. Chrome
                  // currently drops it on ::placeholder (computed value stays
                  // `clip`), so the fallback is a hard clip at the edge, which
                  // is why the placeholders above are also kept short.
                  '&::placeholder': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </TextareaContainer>

            <ButtonRowContainer
              sx={{
                // Shrink-wrapped, and bottom-padded so the 32px controls are
                // centred on the last LINE OF TEXT rather than on the textarea
                // box.
                //
                // The two are not the same, which is what the previous 12px got
                // wrong. flex-end already levels the two boxes at 44px, so a
                // 12px bottom pad left the control sitting at y=0..32, centred
                // at 16. The text sits at y=12..32 (12px top padding, 20px
                // line), centred at 22. The controls rode 6px high against an
                // empty placeholder, which is where it shows worst.
                //
                // 6px puts the control at y=6..38, centre 22, on the text.
                // Derived from the textarea's own metrics: half its 12px top
                // padding, because the control is 32px against a 44px box.
                flex: '0 1 auto',
                width: 'auto',
                minWidth: 0,
                justifyContent: 'flex-end',
                padding: theme.spacing(0, 1.5, 0.75, 0),
              }}
            >
              <LeftButtonsContainer
                sx={{
                  minWidth: 0,
                  // Attach and the live-data chip keep their size; only the
                  // model chip gives ground (see its wrapper below).
                  '& > button': { flexShrink: 0 },
                }}
              >
                {allowAttachments && !liveDataMode && !privateSessionActive && (
                  <InputIconButton
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    title={uploadSupportText ? `Attach file (${uploadSupportText})` : "Attach file"}
                    disabled={disabled}
                  >
                    <AttachFileIcon />
                  </InputIconButton>
                )}

                {liveDataAvailable && (
                  <Tooltip
                    title="Use your connected licensed data source. This run is not saved in chat history."
                    placement="top"
                  >
                    <ModeSelectorButton
                      type="button"
                      active={liveDataMode}
                      disabled={isTaskActive || typeof onLiveDataModeChange !== 'function'}
                      onClick={() => onLiveDataModeChange?.(!liveDataMode)}
                      aria-pressed={liveDataMode}
                      aria-label={liveDataMode ? 'Disable live data mode' : 'Enable live data mode'}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: theme.typography.pxToRem(11),
                          lineHeight: '16px',
                          fontWeight: theme.typography.fontWeightMedium,
                          color: 'inherit',
                        }}
                      >
                        Live data
                      </Typography>
                    </ModeSelectorButton>
                  </Tooltip>
                )}

                {allowModelSelection && !liveDataMode && !privateSessionActive && (
                  <Box
                    sx={{
                      position: "relative",
                      display: 'flex',
                      // The one control on the row that yields. Model names run
                      // from "GLM-5.2" to "Gemini 3.1 Pro Preview", and on a
                      // 320px pane the long ones would otherwise push the send
                      // button past the edge. minWidth 0 down the whole chain
                      // (row, this wrapper, the button, the label) is what lets
                      // the label ellipsize instead of the row overflowing.
                      minWidth: 0,
                      '& .model-toggle': {
                        maxWidth: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                      },
                    }}
                    data-model-dropdown-scope="true"
                  >
                    <Tooltip title={currentModelCopy.tooltip} placement="top">
                      <ModeSelectorButton
                        type="button"
                        active={currentModelKey !== 'default'}
                        disabled={isModelToggleDisabled}
                        className="model-toggle"
                        onClick={handleModelToggle}
                        aria-haspopup="listbox"
                        aria-expanded={showModelDropdown}
                        aria-label={`Model: ${currentModelCopy.label}`}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: theme.typography.pxToRem(11),
                            lineHeight: '16px',
                            fontWeight: theme.typography.fontWeightMedium,
                            color: 'inherit',
                            minWidth: 0,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {currentModelCopy.label}
                        </Typography>
                        <ExpandLessIcon
                          sx={{
                            fontSize: theme.typography.pxToRem(14),
                            color: 'inherit',
                            opacity: 0.7,
                            // The caret is the affordance: the name truncates,
                            // never this.
                            flexShrink: 0,
                            transition: 'transform 160ms ease',
                            transform: showModelDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                          }}
                        />
                      </ModeSelectorButton>
                    </Tooltip>
                    <Popper
                      open={Boolean(modelDropdownAnchorEl) && showModelDropdown}
                      anchorEl={modelDropdownAnchorEl}
                      placement="top-end"
                      modifiers={modelDropdownModifiers}
                      sx={{ zIndex: 1500 }}
                    >
                      <UploadDropdown
                        data-model-dropdown-scope="true"
                        role="listbox"
                        aria-label="Choose a model"
                        // Every row is one line: a name plus its cost marks.
                        // The width floor keeps the marks off the longest name
                        // ("GPT-5.6 Terra") and the ceiling keeps the menu
                        // inside a 375px pane.
                        //
                        // The height cap is new with the sections: three
                        // headings plus nine rows plus the key measures 365px,
                        // which is fine in a normal task pane and is not in a
                        // short one. Capping and scrolling means a short pane
                        // loses the bottom of the list to a scrollbar rather
                        // than to a clip with nothing to indicate it.
                        //
                        // 70vh rather than 60: at 60 the cap lands within a few
                        // pixels of today's content in a 620px pane, so the
                        // scrollbar would blink in and out with small changes
                        // in pane height. 70vh clears the full list from about
                        // 520px of pane upward and still leaves the chat behind
                        // it visible.
                        sx={{
                          minWidth: theme.spacing(27),
                          maxWidth: theme.spacing(32),
                          maxHeight: '70vh',
                          overflowY: 'auto',
                        }}
                      >
                        {modelSections.map((section, sectionIndex) => (
                          <Box
                            key={section.id}
                            role="group"
                            // The group carries the capability word for
                            // assistive tech, and the visible heading below is
                            // aria-hidden so it is not announced twice.
                            aria-label={`${section.label} capability`}
                          >
                            <Box
                              aria-hidden="true"
                              data-section-heading={section.id}
                              sx={{
                                // Everything here is chosen so the heading
                                // cannot be mistaken for a row: it is smaller,
                                // uppercase, letterspaced, muted, has no
                                // hover state and no rounded background, and
                                // it sits closer to the rows it introduces
                                // than to the section above it.
                                padding: theme.spacing(0, 1, 0.25),
                                marginTop: sectionIndex === 0
                                  ? 0
                                  : theme.spacing(1),
                                fontSize: theme.typography.pxToRem(10),
                                fontWeight: 700,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                lineHeight: 1.4,
                                color: alpha(theme.palette.text.secondary, 0.8),
                              }}
                            >
                              {section.label}
                            </Box>

                            {section.options.map((option) => {
                              const isSelected = selectedModel === option.model;
                              // No hover tooltip on the row: the label is the
                              // model's real name, so a popup restating it is
                              // noise in a pane this narrow. The accessible
                              // name repeats the section word rather than
                              // relying on group announcement, which varies by
                              // screen reader, and spells out the cost marks,
                              // which a run of `$` cannot convey on its own.
                              const rowLabel = [
                                option.label,
                                `${section.label} capability`,
                                `cost ${COST_WORDS[option.costLevel] || 'low'}`,
                              ].join('. ');
                              return (
                                <UploadOption
                                  key={option.id}
                                  component="button"
                                  type="button"
                                  role="option"
                                  aria-selected={isSelected}
                                  aria-label={rowLabel}
                                  onClick={() => {
                                    if (onModelChange) {
                                      // The id verbatim. For the Claude
                                      // entries that is an unqualified string;
                                      // for the rest the provider prefix IS
                                      // the routing signal, so anything else
                                      // here silently lands the turn back on
                                      // Claude.
                                      onModelChange(option.model);
                                    }
                                    closeModelDropdown();
                                  }}
                                  sx={{
                                    width: '100%',
                                    border: 'none',
                                    fontFamily: 'inherit',
                                    textAlign: 'left',
                                    alignItems: 'center',
                                    gap: theme.spacing(1.25),
                                    padding: theme.spacing(0.625, 1),
                                    backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      // The name is the whole row now, so it
                                      // is the visual anchor and it truncates
                                      // rather than wraps: this pane can be
                                      // 320px wide and a two-line model name
                                      // would break the list's rhythm.
                                      fontSize: theme.typography.pxToRem(13),
                                      fontWeight: isSelected
                                        ? theme.typography.fontWeightSemiBold || 600
                                        : theme.typography.fontWeightMedium,
                                      color: isSelected ? 'primary.main' : 'text.primary',
                                      lineHeight: 1.4,
                                      letterSpacing: '-0.005em',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {option.label}
                                  </Typography>
                                  {renderCostMarks(option.costLevel, isSelected)}
                                </UploadOption>
                              );
                            })}
                          </Box>
                        ))}

                        {/* The only key the one remaining signal needs.
                            Capability is no longer mentioned here because it
                            is no longer drawn on a row: the headings say it. */}
                        <Box
                          aria-hidden="true"
                          sx={{
                            margin: theme.spacing(1, 1, 0),
                            paddingTop: theme.spacing(0.75),
                            borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
                            fontSize: theme.typography.pxToRem(10),
                            lineHeight: 1.4,
                            color: alpha(theme.palette.text.secondary, 0.85),
                          }}
                        >
                          $ shows cost, from $ to $$$$
                        </Box>
                      </UploadDropdown>
                    </Popper>
                  </Box>
                )}

                {/* No Credit Card Message (for landing page) */}
                {showNoCreditCardMessage && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.5)'
                        : 'rgba(0, 0, 0, 0.45)',
                      gap: 0.5,
                      ml: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: theme.typography.pxToRem(11),
                        fontWeight: theme.typography.fontWeightRegular,
                        color: 'inherit',
                        lineHeight: 1,
                      }}
                    >
                      No credit card required
                    </Typography>
                  </Box>
                )}
              </LeftButtonsContainer>

              <RightButtonsContainer>
                <InputIconButton
                  type="button"
                  // Stop control shows while our own turn runs (stops it) and
                  // while a peer app works on our request with nothing typed
                  // (dismisses the wait). Typing flips it back to Send so the
                  // user can steer or queue a message at any time.
                  onClick={showStopControl
                    ? (isOwnTurnActive ? onStopRequest : () => onCancelPeerWait?.())
                    : () => performSend()}
                  disabled={disabled || (!showStopControl && isSendDisabled)}
                  className={showStopControl ? "stop-button" : "send-button"}
                  aria-label={showStopControl
                    ? (isOwnTurnActive ? "Stop current request" : "Stop waiting for Office tools")
                    : isOwnTurnActive
                      ? "Send instructions to the current request"
                      : "Send message"}
                  title={showStopControl
                    ? (isOwnTurnActive ? "Stop current request (Esc)" : "Stop waiting for Office tools")
                    : isOwnTurnActive
                      ? "Send instructions to the current request"
                      : "Send message"}
                  sx={
                    showStopControl
                      ? {
                          // Clean stop control: solid, no pulse animation. The
                          // "still running" signal is the elapsed timer on the
                          // status line, under the newest tool card.
                          position: 'relative',
                          zIndex: 2,
                          backgroundColor: theme.palette.error.main,
                          color: theme.palette.error.contrastText,
                          "&:hover": {
                            backgroundColor: theme.palette.error.dark,
                          },
                        }
                      : (disabled || (isSendDisabled && !isLoading))
                        ? {
                            backgroundColor: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                          }
                        : {
                            backgroundColor: '#B83B1D',
                            color: '#ffffff',
                            border: 'none',
                            transition: 'transform 140ms ease, background-color 140ms ease, box-shadow 140ms ease',
                            "&:hover": {
                              backgroundColor: '#A33418',
                              transform: 'translateY(-1px)',
                              boxShadow: '0 3px 10px rgba(184, 59, 29, 0.28)',
                            },
                            "&:active": {
                              transform: 'translateY(0)',
                              boxShadow: '0 1px 4px rgba(184, 59, 29, 0.22)',
                            },
                          }
                  }
                >
                  {showStopControl ? (
                    <StopIcon />
                  ) : (
                    <ArrowUpwardIcon />
                  )}
                </InputIconButton>
              </RightButtonsContainer>
            </ButtonRowContainer>
          </InputRowContainer>

          {/* Slash-command menu: workflows + /compact, filtered as the user
              types after the leading '/'. Compact single-line rows (name +
              dimmed one-liner, Claude Code style) inside a scrollable,
              height-capped list, so the top entries (/compact first) can
              never be clipped off-screen by a tall menu. Click inserts the
              command. */}
          <Popper
            open={slashActive && slashItems.length > 0}
            anchorEl={inputRowRef.current}
            placement="top-start"
            modifiers={uploadDropdownModifiers}
            style={{ zIndex: 1400 }}
          >
            <Box
              sx={{
                // Match the composer's full width so command names and their
                // one-line descriptions get as much room as the pane allows.
                width: inputRowRef.current ? `${inputRowRef.current.offsetWidth}px` : 'auto',
                minWidth: 260,
                maxHeight: 288,
                overflowY: 'auto',
                borderRadius: chatRadii.card,
                border: chatBorders.resting,
                backgroundColor: theme.palette.background.paper,
                boxShadow: theme.shadows[4],
                py: 0.5,
              }}
            >
              {slashItems.map((item) => (
                <Box
                  key={item.name}
                  component="button"
                  type="button"
                  onClick={() => {
                    if (item.builtin && item.name === 'compact') {
                      onCompactChat?.();
                      setInput('');
                      return;
                    }
                    setInput(`/${item.name} `);
                    try { textareaRef.current?.focus(); } catch (e) { /* ignore */ }
                  }}
                  sx={{
                    display: 'flex', alignItems: 'baseline', gap: 1,
                    width: '100%', textAlign: 'left', appearance: 'none',
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    px: 1.25, py: 0.55, minWidth: 0,
                    '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.05) },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      fontSize: theme.typography.pxToRem(12.5),
                      color: 'text.primary',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    }}
                  >
                    /{item.name}
                  </Typography>
                  {item.description && (
                    <Typography
                      variant="caption"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: theme.typography.pxToRem(11),
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.description}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Popper>
        </InputContainer>
      </Box>

      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileUpload}
        multiple
        accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.txt,.md,.png,.jpg,.jpeg,.gif,.bmp,.webp,.svg,image/*"
        style={{ display: "none" }}
      />


    </ChatInput>
  );
};

export default AIInputSurface;
